import React, { useState, useEffect, useCallback } from "react";
import MainLayout from "../Themes/MainLayout";
import {
  Box, Typography, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select,
  FormControl, InputLabel, Chip, Tooltip, CircularProgress,
  Alert, Paper, LinearProgress, Tabs, Tab, Stack, Popover,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import {
  Add, Edit, Delete, ArrowBackIos, ArrowForwardIos,
  CalendarToday, AccessTime, CheckCircle, Cancel,
  HourglassEmpty, Warning, BeachAccess, Send, Celebration,
} from "@mui/icons-material";
import { useCurrentUser } from "../utils/useCurrentUser";
import { getStartOfWeek, formatDateRange } from "../utils/dateUtils";

const BASE_URL       = process.env.REACT_APP_API_BASE_URL;
const WEEKDAYS_SHORT = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS         = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const HOUR_OPTIONS   = Array.from({ length:97 }, (_,i) => (i*0.25).toFixed(2));

// Convert decimal hours to "Xhr Ymins" display format.
// compact=true → "9h 45m" (for chips/cells), false → "9 hrs 45 mins" (for headers)
function fmtHrs(decimal, compact = false) {
  const total = Math.round(parseFloat(decimal || 0) * 60);
  const h     = Math.floor(total / 60);
  const m     = total % 60;
  if (compact) {
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  }
  if (h === 0) return `${m} min${m !== 1 ? "s" : ""}`;
  if (m === 0) return `${h} hr${h !== 1 ? "s" : ""}`;
  return `${h} hr${h !== 1 ? "s" : ""} ${m} min${m !== 1 ? "s" : ""}`;
}

// ─── helpers ──────────────────────────────────────────────────────────────────
const toLocalStr = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
};
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate()+n); return r; };
const isToday = (s) => toLocalStr(new Date()) === s;

const isSubmittedStatus = (s) => {
  if (!s) return false;
  const lower = s.toLowerCase().trim();
  // "Pending" alone = not yet submitted (draft state, editable)
  // "Rejected" = sent back by approver, employee can re-edit
  // Everything else = in the approval pipeline → locked
  if (lower === "pending" || lower === "rejected") return false;
  return true;
};

// Per-project lock: a specific proj+task row is locked if its status is submitted
const isRowLocked = (status) => isSubmittedStatus(status);

// ─── StatusChip ───────────────────────────────────────────────────────────────
const STATUS_CFG = {
  Pending:   { color:"warning", icon:<HourglassEmpty sx={{fontSize:12}}/> },
  Submitted: { color:"info",    icon:<Send           sx={{fontSize:12}}/> },
  Approved:  { color:"success", icon:<CheckCircle    sx={{fontSize:12}}/> },
  Rejected:  { color:"error",   icon:<Cancel         sx={{fontSize:12}}/> },
};
function StatusChip({ status }) {
  if (!status) return null;
  const key = Object.keys(STATUS_CFG).find(k => status.toLowerCase().startsWith(k.toLowerCase())) || "Pending";
  const cfg = STATUS_CFG[key];
  return (
    <Chip size="small" color={cfg.color} icon={cfg.icon}
      label={status.length > 24 ? status.slice(0,24)+"…" : status}
      sx={{ fontSize:"0.63rem", height:20, maxWidth:180 }}/>
  );
}

// ─── Jira-style Week Picker ───────────────────────────────────────────────────
function WeekPicker({ weekStart, onChange, anchorEl, onClose }) {
  const [view, setView] = useState(new Date(weekStart));
  const firstOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const startPad     = firstOfMonth.getDay();
  const daysInMonth  = new Date(view.getFullYear(), view.getMonth()+1, 0).getDate();
  const cells = [...Array(startPad).fill(null),
    ...Array.from({length:daysInMonth},(_,i)=>new Date(view.getFullYear(),view.getMonth(),i+1))];
  const ws = getStartOfWeek(weekStart);
  const we = addDays(ws, 6);
  const inSel = (d) => d && d >= ws && d <= we;
  const isWS  = (d) => d && toLocalStr(d) === toLocalStr(ws);
  const isWE  = (d) => d && toLocalStr(d) === toLocalStr(we);

  return (
    <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClose}
      anchorOrigin={{vertical:"bottom",horizontal:"left"}}
      PaperProps={{sx:{borderRadius:2,boxShadow:"0 8px 32px rgba(0,0,0,0.18)",minWidth:280,overflow:"hidden"}}}>
      <Box sx={{bgcolor:"#0052CC",color:"#fff",px:2,py:1.5,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <IconButton size="small" onClick={()=>setView(new Date(view.getFullYear(),view.getMonth()-1,1))} sx={{color:"#fff"}}>
          <ArrowBackIos sx={{fontSize:13}}/>
        </IconButton>
        <Typography fontWeight={700} fontSize="0.9rem">{MONTHS[view.getMonth()]} {view.getFullYear()}</Typography>
        <IconButton size="small" onClick={()=>setView(new Date(view.getFullYear(),view.getMonth()+1,1))} sx={{color:"#fff"}}>
          <ArrowForwardIos sx={{fontSize:13}}/>
        </IconButton>
      </Box>
      <Box sx={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",px:1,pt:1,bgcolor:"#fff"}}>
        {WEEKDAYS_SHORT.map(d=>(
          <Typography key={d} variant="caption" align="center"
            sx={{color:"#5E6C84",fontWeight:700,fontSize:"0.65rem",pb:0.5}}>{d}</Typography>
        ))}
      </Box>
      <Box sx={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",px:1,pb:1.5,bgcolor:"#fff"}}>
        {cells.map((d,i)=>{
          const sel=inSel(d), start=isWS(d), end=isWE(d), tod=d&&isToday(toLocalStr(d));
          return (
            <Box key={i} onClick={()=>{if(d){onChange(getStartOfWeek(d));onClose();}}}
              sx={{
                height:32,display:"flex",alignItems:"center",justifyContent:"center",
                cursor:d?"pointer":"default",
                bgcolor:sel?"#0052CC":"transparent",
                borderRadius:start?"50% 0 0 50%":end?"0 50% 50% 0":0,
                ...(start&&end&&{borderRadius:"50%"}),
                "&:hover":d?{bgcolor:sel?"#0065FF":"#EBECF0",
                  borderRadius:start?"50% 0 0 50%":end?"0 50% 50% 0":sel?0:"4px"}:{},
              }}>
              {d&&(
                <Typography variant="caption" sx={{
                  fontWeight:tod?800:sel?600:400,
                  color:sel?"#fff":tod?"#0052CC":"#172B4D",
                  fontSize:"0.8rem",lineHeight:1,
                  border:tod&&!sel?"2px solid #0052CC":"none",
                  borderRadius:"50%",width:22,height:22,
                  display:"flex",alignItems:"center",justifyContent:"center",
                }}>{d.getDate()}</Typography>
              )}
            </Box>
          );
        })}
      </Box>
      <Box sx={{borderTop:"1px solid #EBECF0",px:2,py:1,display:"flex",justifyContent:"space-between",alignItems:"center",bgcolor:"#fff"}}>
        <Button size="small" sx={{textTransform:"none",fontSize:"0.75rem",color:"#0052CC"}}
          onClick={()=>{onChange(getStartOfWeek(new Date()));onClose();}}>This week</Button>
        <Typography variant="caption" color="text.secondary">Week of {toLocalStr(ws)}</Typography>
      </Box>
    </Popover>
  );
}

// ─── Log Time Dialog ──────────────────────────────────────────────────────────
function LogTimeDialog({ open, onClose, onSave, clientProjects, initialData, saveError, saving, boId }) {
  const today = toLocalStr(new Date());
  const empty = { client_id:"", proj_id:"", task:"", project_task_id:null, date:today, hours:"1.00", notes:"" };
  const [form, setForm] = useState(empty);
  const [taskOptions, setTaskOptions] = useState([]); // predefined tasks for the chosen project
  useEffect(() => { if (open) setForm(initialData ? {...initialData} : empty); }, [open]);

  const clients  = [...new Map(clientProjects.map(cp=>[cp.cl_id,cp.cl_name])).entries()];
  const projects = clientProjects.filter(cp=>cp.cl_id===form.client_id);
  const set = (f) => (e) => {
    const v = e?.target ? e.target.value : e;
     setForm(p=>({...p,[f]:v,...(f==="client_id"?{proj_id:"",project_task_id:null}:{}),...(f==="proj_id"?{project_task_id:null}:{})}));
  };

    // Load the chosen project's predefined task list (pick-or-type source).
  useEffect(() => {
    if (!open || !boId || !form.client_id || !form.proj_id) { setTaskOptions([]); return; }
    let cancelled = false;
    fetch(`${BASE_URL}/project-tasks/${boId}/${form.client_id}/${form.proj_id}`)
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => { if (!cancelled) setTaskOptions(Array.isArray(d?.data) ? d.data : []); })
      .catch(() => { if (!cancelled) setTaskOptions([]); });
    return () => { cancelled = true; };
  }, [open, boId, form.client_id, form.proj_id]);
  const valid = form.proj_id && form.task.trim() && parseFloat(form.hours)>0 && form.date;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{sx:{borderRadius:2}}}>
      <DialogTitle sx={{pb:1,borderBottom:"1px solid #EBECF0"}}>
        <Stack direction="row" alignItems="center" gap={1}>
          <AccessTime sx={{color:"#0052CC"}}/>
          <Typography fontWeight={700}>{initialData?.timesheet_id?"Edit Time Log":"Log Time"}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{pt:2}}>
        <Stack spacing={2} mt={0.5}>
          {saveError && (
            <Alert severity="error" sx={{fontSize:"0.82rem"}}>
              {saveError}
            </Alert>
          )}
          <FormControl fullWidth size="small">
            <InputLabel>Client</InputLabel>
            <Select value={form.client_id} label="Client" onChange={set("client_id")}>
              {clients.map(([id,name])=><MenuItem key={id} value={id}>{name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" disabled={!form.client_id}>
            <InputLabel>Project</InputLabel>
            <Select value={form.proj_id} label="Project" onChange={set("proj_id")}>
              {projects.map(p=><MenuItem key={p.proj_id} value={p.proj_id}>{p.proj_name}</MenuItem>)}
            </Select>
          </FormControl>
          {/* Project Task — dropdown of this project's defined tasks (e.g. "Sub Link – BAU") */}
          <FormControl fullWidth size="small" disabled={!form.proj_id}>
            <InputLabel>Project Task</InputLabel>
            <Select
              label="Project Task"
              value={form.project_task_id || ""}
              onChange={(e) =>
                setForm((p) => ({ ...p, project_task_id: e.target.value || null }))
              }
            >
              {taskOptions.length === 0 ? (
                <MenuItem value="" disabled>
                  {form.proj_id ? "No tasks defined for this project" : "Choose a project first"}
                </MenuItem>
              ) : (
                taskOptions.map((o) => (
                  <MenuItem key={o.task_id} value={o.task_id}>
                    {o.display_label || o.task_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <TextField label="Task / Description" size="small" fullWidth multiline rows={2}
            value={form.task} onChange={set("task")} placeholder="What did you work on?"/>
          <Stack direction="row" spacing={2}>
            <TextField label="Date" type="date" size="small" fullWidth
              value={form.date} onChange={set("date")} InputLabelProps={{shrink:true}}/>
            <FormControl size="small" sx={{minWidth:130}}>
              <InputLabel>Hours Spent</InputLabel>
              <Select value={form.hours} label="Hours Spent" onChange={set("hours")}>
                {HOUR_OPTIONS.filter(h=>parseFloat(h)>0).map(h=>(
                  <MenuItem key={h} value={h}>{h}h</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField label="Notes (optional)" size="small" fullWidth
            value={form.notes} onChange={set("notes")} placeholder="Any additional notes..."/>
        </Stack>
      </DialogContent>
      <DialogActions sx={{px:3,pb:2,borderTop:"1px solid #EBECF0",pt:1.5}}>
        <Button onClick={onClose} color="inherit" sx={{textTransform:"none"}}>Cancel</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={()=>onSave(form)}
          sx={{textTransform:"none",bgcolor:"#0052CC","&:hover":{bgcolor:"#0065FF"}}}>
          {saving ? "Saving..." : initialData?.timesheet_id?"Save Changes":"Log Time"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Weekly Summary bar ───────────────────────────────────────────────────────
function WeeklySummary({ dayTotals, leaveDays, holidayDays, weekDates }) {
  // Only count leave/holiday hours on working days (Mon–Fri, day indices 1–5).
  // Holidays or leaves that fall on weekends are already non-working and must not
  // reduce the 40-hour weekly target.
  const totalLeave   = parseFloat(
    Object.entries(leaveDays)
      .filter(([off]) => parseInt(off) >= 1 && parseInt(off) <= 5)
      .reduce((s,[,h])=>s+h,0)
      .toFixed(2)
  );
  const totalHoliday = parseFloat(
    Object.entries(holidayDays)
      .filter(([off]) => parseInt(off) >= 1 && parseInt(off) <= 5)
      .reduce((s,[,h])=>s+h,0)
      .toFixed(2)
  );
  const totalDeduct  = parseFloat((totalLeave + totalHoliday).toFixed(2));
  const required     = Math.max(0, parseFloat((40 - totalDeduct).toFixed(2)));
  const totalLogged  = dayTotals.reduce((a,b)=>a+b,0);
  const pct          = required>0 ? Math.min((totalLogged/required)*100,100) : 100;
  const met          = totalLogged >= required;

  return (
    <Paper elevation={0} sx={{p:1.5,mb:1.5,border:"1px solid #DFE1E6",borderRadius:2,bgcolor:"#FAFBFC"}}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
        <Typography variant="body2" fontWeight={700} color="#172B4D">Weekly Progress</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {totalHoliday>0&&(
            <Chip icon={<Celebration sx={{fontSize:13}}/>} label={`${totalHoliday}h holiday`}
              size="small" sx={{bgcolor:"#EAE6FF",color:"#403294",fontSize:"0.7rem",height:22}}/>
          )}
          {totalLeave>0&&(
            <Chip icon={<BeachAccess sx={{fontSize:13}}/>} label={`${totalLeave}h leave`}
              size="small" sx={{bgcolor:"#FFF0B3",color:"#FF8B00",fontSize:"0.7rem",height:22}}/>
          )}
          <Typography variant="body2" fontWeight={700} color={met?"#006644":"#5E6C84"}>
            {fmtHrs(totalLogged)} / {fmtHrs(required)}
          </Typography>
          {met&&<CheckCircle sx={{color:"#36B37E",fontSize:18}}/>}
        </Stack>
      </Box>
      <LinearProgress variant="determinate" value={pct}
        color={met?"success":totalLogged>=required*0.5?"warning":"error"}
        sx={{height:6,borderRadius:4,bgcolor:"#EBECF0"}}/>
    </Paper>
  );
}

// ─── Approval chain label helper ─────────────────────────────────────────────
function getApprovalChainInfo(status, approvalLevel) {
  if (!status) return null;
  const s = status.toLowerCase().trim();
  const lvl = parseInt(approvalLevel) || 1;

  // Build the chain steps based on approval_level_required
  const steps = ["TL"];
  if (lvl >= 2) steps.push("PM");
  if (lvl >= 3) steps.push("HR");

  if (s === "pending")    return { step: 0, total: steps.length, steps, label: "Draft — not yet submitted" };
  if (s === "submitted")  return { step: 0, total: steps.length, steps, nextRole: "Technical Lead",  label: `Waiting for TL approval` };
  if (s === "rejected")   return { step: -1, total: steps.length, steps, label: "Rejected — edit and resubmit" };
  if (s === "approved")   return { step: steps.length, total: steps.length, steps, label: "Fully Approved" };

  const m1 = s.match(/^pending,\s*1 of (\d+)/);
  if (m1) return { step: 1, total: steps.length, steps, nextRole: "Project Manager", label: "TL approved — Waiting for PM" };
  const m2 = s.match(/^pending,\s*2 of (\d+)/);
  if (m2) return { step: 2, total: steps.length, steps, nextRole: "HR", label: "TL + PM approved — Waiting for HR" };

  if (s === "waiting for pm approval")          return { step: 1, total: steps.length, steps, nextRole: "Project Manager", label: "Waiting for PM approval" };
  if (s === "waiting for hr approval")          return { step: 2, total: steps.length, steps, nextRole: "HR",              label: "Waiting for HR approval" };
  if (s === "waiting for admin approval")       return { step: steps.length, total: steps.length, steps, nextRole: "Admin",        label: "Waiting for Admin approval" };
  if (s === "waiting for system admin approval")return { step: steps.length, total: steps.length, steps, nextRole: "System Admin", label: "Waiting for System Admin approval" };

  return { step: 0, total: steps.length, steps, label: status };
}

// ─── Per-project approval progress bar ───────────────────────────────────────
function ApprovalProgressBar({ status, approvalLevel }) {
  const info = getApprovalChainInfo(status, approvalLevel);
  if (!info) return null;

  const isApproved = status?.toLowerCase() === "approved";
  const isRejected = status?.toLowerCase() === "rejected";

  return (
    <Box sx={{ mt: 0.75 }}>
      {/* Step dots */}
      <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
        {info.steps.map((step, i) => {
          const done    = i < info.step;
          const current = i === info.step && !isApproved && !isRejected;
          return (
            <React.Fragment key={step}>
              <Tooltip title={`${step}: ${done ? "Approved" : current ? "Pending" : "Waiting"}`}>
                <Box sx={{
                  width: 20, height: 20, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  bgcolor: isRejected ? "#FFBDAD" : done || isApproved ? "#36B37E" : current ? "#0052CC" : "#DFE1E6",
                  border: current ? "2px solid #0052CC" : "none",
                  flexShrink: 0,
                }}>
                  {(done || isApproved) && !isRejected
                    ? <CheckCircle sx={{ fontSize: 12, color: "#fff" }} />
                    : isRejected
                    ? <Cancel sx={{ fontSize: 12, color: "#DE350B" }} />
                    : <Typography sx={{ fontSize: "0.55rem", color: current ? "#fff" : "#97A0AF", fontWeight: 700 }}>
                        {i + 1}
                      </Typography>
                  }
                </Box>
              </Tooltip>
              {i < info.steps.length - 1 && (
                <Box sx={{ flex: 1, height: 2, bgcolor: done || isApproved ? "#36B37E" : "#DFE1E6", borderRadius: 1 }} />
              )}
            </React.Fragment>
          );
        })}
      </Stack>
      {/* Label */}
      <Typography sx={{
        fontSize: "0.62rem", fontWeight: 600,
        color: isApproved ? "#006644" : isRejected ? "#DE350B" : "#5E6C84",
      }}>
        {info.label}
      </Typography>
    </Box>
  );
}

// ─── Project approval summary panel (below grid) ──────────────────────────────
function ProjectApprovalSummary({ entries }) {
  // Group by project, get status + approval_level_required
  const projMap = new Map();
  entries.forEach(e => {
    if (!projMap.has(e.proj_id)) {
      projMap.set(e.proj_id, {
        proj_name: e.proj_name,
        cl_name: e.cl_name,
        approval_status: e.approval_status,
        approval_level_required: e.approval_level_required,
      });
    }
  });
  const projects = Array.from(projMap.values());
  if (!projects.length) return null;

  return (
    <Paper elevation={0} sx={{ border: "1px solid #DFE1E6", borderRadius: 2, p: 2, mt: 1.5 }}>
      <Typography variant="body2" fontWeight={700} color="#172B4D" mb={1.5}>
        Approval Status by Project
      </Typography>
      <Stack spacing={2}>
        {projects.map((p, i) => (
          <Box key={i} sx={{
            p: 1.5, borderRadius: 1.5,
            border: "1px solid",
            borderColor: p.approval_status === "Approved" ? "#ABF5D1"
              : p.approval_status === "Rejected" ? "#FFBDAD" : "#DFE1E6",
            bgcolor: p.approval_status === "Approved" ? "#E3FCEF"
              : p.approval_status === "Rejected" ? "#FFFAE6" : "#FAFBFC",
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="body2" fontWeight={700} color="#0052CC">{p.proj_name}</Typography>
                {p.cl_name && (
                  <Typography variant="caption" color="#97A0AF">{p.cl_name}</Typography>
                )}
              </Box>
              <StatusChip status={p.approval_status} />
            </Box>
            <ApprovalProgressBar
              status={p.approval_status}
              approvalLevel={p.approval_level_required}
            />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}
function TimesheetGrid({ entries, weekDates, leaveDays, holidayDays, onAdd, onEdit, onDelete, weekIsLocked }) {
  // Best default date: latest weekday in this week that is ≤ today, else the first weekday (Mon).
  const smartDefaultDate = (() => {
    const today = toLocalStr(new Date());
    // Walk backwards from Saturday to find the latest date ≤ today
    for (let i = weekDates.length - 1; i >= 0; i--) {
      if (weekDates[i] <= today) return weekDates[i];
    }
    // Whole week is in the future — return Monday (index 1)
    return weekDates[1] || weekDates[0];
  })();

  // Most-recently-logged entry this week (used to pre-fill new rows)
  const lastEntry = entries.length
    ? [...entries].sort((a, b) =>
        new Date(b.timesheet_date) - new Date(a.timesheet_date)
      )[0]
    : null;

  // Group entries by proj_id + task_description
  const rowMap = new Map();
  entries.forEach(e => {
    const key = `${e.proj_id}||${e.task_description}`;
    if (!rowMap.has(key)) {
      rowMap.set(key, {
        proj_id: e.proj_id,
        cl_id: e.cl_id,
        proj_name: e.proj_name,
        cl_name: e.cl_name,
        task: e.task_description,
        taskLabel: e.pt_display_label || null,
        project_task_id: e.project_task_id || null,
        approval_level_required: e.approval_level_required,
        cells: {},
      });
    }
    const dateStr = toLocalStr(new Date(e.timesheet_date));
    rowMap.get(key).cells[dateStr] = e;
  });
  const rows = Array.from(rowMap.values());

  // Column widths
  const COL_TASK = 260;
  const COL_DAY  = 90;
  const COL_TOTAL= 72;
  const COL_ACT  = 60;

  const cellSx = {
    px:1, py:0.5, borderBottom:"1px solid #EBECF0", verticalAlign:"middle",
  };
  const headSx = {
    ...cellSx, bgcolor:"#F4F5F7", fontWeight:700, fontSize:"0.72rem",
    color:"#5E6C84", whiteSpace:"nowrap",
  };

  return (
    <Paper elevation={0} sx={{border:"1px solid #DFE1E6",borderRadius:2,overflow:"hidden"}}>
      <Box sx={{overflowX:"auto"}}>
        <Table size="small" sx={{minWidth:900,tableLayout:"fixed"}}>
          <TableHead>
            <TableRow>
              {/* Task column */}
              <TableCell sx={{...headSx, width:COL_TASK, minWidth:COL_TASK}}>
                Project / Task
              </TableCell>
              {/* Day columns */}
              {weekDates.map((d,i)=>{
                const lh  = leaveDays[i]||0;
                const hh  = holidayDays[i]||0;
                const tod = isToday(d);
                const dt  = new Date(d+"T00:00:00");
                return (
                  <TableCell key={d} align="center"
                    sx={{...headSx, width:COL_DAY, minWidth:COL_DAY,
                      bgcolor:tod?"#E9F2FF":hh>=8?"#EAE6FF":lh>=8?"#FFF7E6":"#F4F5F7",
                      color:tod?"#0052CC":"#5E6C84",
                      borderLeft:"1px solid #EBECF0",
                    }}>
                    <Box>
                      <Typography sx={{fontSize:"0.68rem",fontWeight:700,textTransform:"uppercase",letterSpacing:0.4}}>
                        {WEEKDAYS_SHORT[dt.getDay()]}
                      </Typography>
                      <Typography sx={{fontSize:"0.82rem",fontWeight:800,color:tod?"#0052CC":"#172B4D"}}>
                        {dt.getDate()}
                      </Typography>
                      {hh>=8&&(
                        <Tooltip title="Public holiday — you may still log time if you worked">
                          <Chip icon={<Celebration sx={{fontSize:9}}/>} label="Holiday"
                            size="small" sx={{height:16,fontSize:"0.58rem",bgcolor:"#EAE6FF",color:"#403294",mt:0.25,cursor:"help"}}/>
                        </Tooltip>
                      )}
                      {hh>0&&hh<8&&(
                        <Tooltip title={`${hh}h public holiday — deducted from weekly target`}>
                          <Typography sx={{fontSize:"0.58rem",color:"#403294",cursor:"help"}}>-{hh}h holiday</Typography>
                        </Tooltip>
                      )}
                      {lh>=8&&(
                        <Tooltip title="Approved leave — time logging disabled">
                          <Chip icon={<BeachAccess sx={{fontSize:9}}/>} label="Leave"
                            size="small" sx={{height:16,fontSize:"0.58rem",bgcolor:"#FFF0B3",color:"#FF8B00",mt:0.25,cursor:"help"}}/>
                        </Tooltip>
                      )}
                      {lh>0&&lh<8&&(
                        <Tooltip title={`${lh}h approved leave — only ${8-lh}h available`}>
                          <Typography sx={{fontSize:"0.58rem",color:"#FF8B00",cursor:"help"}}>-{lh}h leave</Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
              {/* Total */}
              <TableCell align="center" sx={{...headSx, width:COL_TOTAL, borderLeft:"2px solid #DFE1E6"}}>
                Total
              </TableCell>
              {/* Actions */}
              {!weekIsLocked&&(
                <TableCell align="center" sx={{...headSx, width:COL_ACT}}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length===0?(
              <TableRow>
                <TableCell colSpan={weekDates.length+3} sx={{textAlign:"center",py:5,color:"#97A0AF"}}>
                  <AccessTime sx={{fontSize:32,color:"#DFE1E6",display:"block",mx:"auto",mb:1}}/>
                  <Typography variant="body2" color="#97A0AF">No time logged this week</Typography>
                  <Typography variant="caption" color="#C1C7D0">Click "+ Log Time" to get started</Typography>
                </TableCell>
              </TableRow>
            ):(
              rows.map((row,ri)=>{
                const rowTotal = weekDates.reduce((s,d)=>{
                  const e = row.cells[d];
                  return s + (e ? parseFloat(e.effort_hours||0) : 0);
                },0);
                // Row status — use the first entry's status
                const firstEntry = Object.values(row.cells)[0];
                const rowStatus  = firstEntry?.approval_status;
                // Per-project locking: this row is locked only if its own status is in the pipeline
                const rowLocked  = weekIsLocked && isRowLocked(rowStatus);
                const canEditRow = !rowLocked && (rowStatus==="Pending"||rowStatus==="Rejected");

                return (
                  <TableRow key={ri} sx={{
                    "&:hover":{bgcolor:"#F8F9FA"},
                    bgcolor:rowStatus==="Rejected"?"#FFFAE6":rowStatus==="Approved"?"#F3FFF8":"inherit",
                  }}>
                    {/* Task cell */}
                    <TableCell sx={{...cellSx, width:COL_TASK}}>
                      <Box>
                        <Typography variant="caption" fontWeight={700} color="#0052CC"
                          sx={{display:"block",lineHeight:1.3}}>
                          {row.proj_name}
                        </Typography>
                                                {/* Project Task badge (Sub Link – BAU) */}
                        {row.taskLabel && (
                          <Typography variant="caption" fontWeight={700}
                            sx={{
                              display:"inline-block", lineHeight:1.3, mt:0.25,
                              bgcolor: 50,
                              color: 700,
                              borderRadius: "4px",
                              px: 0.75, py: 0.1,
                              fontSize: "0.68rem",
                            }}>
                            {row.taskLabel}
                          </Typography>
                        )}
                        {/* For rows with a project task, descriptions vary per day —
                            they are shown as tooltips on each day's chip below.
                            For legacy rows (no taskLabel), show the shared description here. */}
                        {!row.taskLabel && (
                          <Typography variant="caption" color= '#0052CC'
                            sx={{display:"block",lineHeight:1.3,mt:0.25,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            {row.task}
                          </Typography>
                        )}
                        <Typography variant="caption" color="#5E6C84"
                          sx={{display:"block",lineHeight:1.3,mt:0.25,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {row.task}
                        </Typography>
                        {row.cl_name&&(
                          <Typography variant="caption" color="#97A0AF" sx={{fontSize:"0.6rem",display:"block"}}>
                            {row.cl_name}
                          </Typography>
                        )}
                        {/* status chip removed — overall status shown in header */}                      </Box>
                    </TableCell>

                    {/* Day cells */}
                    {weekDates.map((d,i)=>{
                      const e          = row.cells[d];
                      const lh         = leaveDays[i]||0;
                      const hh         = holidayDays[i]||0;
                      const isLeave    = lh>=8;           // leave locks the cell
                      const isHoliday  = hh>=8;           // holiday shows warning only — NOT locked
                      const tod        = isToday(d);
                      const hrs        = e ? parseFloat(e.effort_hours||0) : 0;

                      return (
                        <TableCell key={d} align="center"
                          sx={{...cellSx, borderLeft:"1px solid #EBECF0",
                            bgcolor:isHoliday&&!isLeave?"#F3F0FF":isLeave?"#FFF7E6":tod?"#F0F7FF":"inherit",
                            minWidth:COL_DAY,
                          }}>
                          {isLeave?(
                            // Leave — fully locked
                            <Tooltip title="Approved leave — time logging disabled for this date">
                              <Box sx={{display:"flex",flexDirection:"column",alignItems:"center",gap:0.25}}>
                                <Typography sx={{fontSize:"0.75rem",color:"#FFB347"}}>🏖</Typography>
                                <Typography sx={{fontSize:"0.58rem",color:"#FF8B00",fontWeight:600,lineHeight:1}}>Locked</Typography>
                              </Box>
                            </Tooltip>
                          ):e?(
                            // Has a time entry — show hours chip + optional holiday badge
                            <Box>
                              {isHoliday&&(
                                <Tooltip title="Public holiday — hours logged on a holiday">
                                  <Typography sx={{fontSize:"0.65rem",color:"#403294",mb:0.25}}>🎉</Typography>
                                </Tooltip>
                              )}
                              <Chip label={fmtHrs(hrs, true)} size="small"
                                sx={{fontSize:"0.68rem",height:20,fontWeight:700,
                                  bgcolor:hrs>0?"#DEEBFF":"#F4F5F7",
                                  color:hrs>0?"#0052CC":"#97A0AF",
                                  cursor:canEditRow?"pointer":"default",
                                }}
                                onClick={canEditRow?()=>onEdit(e):undefined}
                              />
                              {e.notes&&(
                                <Tooltip title={e.notes}>
                                  <Typography sx={{fontSize:"0.6rem",color:"#97A0AF",mt:0.25,cursor:"help"}}>
                                    📝
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          ):canEditRow?(
                            // No entry yet — show add button; on holiday show a subtle warning badge too
                            <Box sx={{display:"flex",flexDirection:"column",alignItems:"center",gap:0.25}}>
                              {isHoliday&&(
                                <Tooltip title="Public holiday — you can still log time if you worked">
                                  <Typography sx={{fontSize:"0.65rem",color:"#403294"}}>🎉</Typography>
                                </Tooltip>
                              )}
                              <Tooltip title={`Log time for ${WEEKDAYS_SHORT[new Date(d+"T00:00:00").getDay()]}${isHoliday?" (public holiday)":""}`}>
                                <IconButton size="small" onClick={()=>onAdd(d, row)}
                                  sx={{color:"#C1C7D0","&:hover":{color:"#0052CC",bgcolor:"#DEEBFF"},p:0.5}}>
                                  <Add sx={{fontSize:16}}/>
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ):(
                            <Typography sx={{fontSize:"0.75rem",color:"#DFE1E6"}}>—</Typography>
                          )}
                        </TableCell>
                      );
                    })}

                    {/* Row total */}
                    <TableCell align="center"
                      sx={{...cellSx, borderLeft:"2px solid #DFE1E6", fontWeight:700,
                        fontSize:"0.8rem", color:rowTotal>0?"#172B4D":"#97A0AF"}}>
                      {rowTotal>0?fmtHrs(rowTotal, true):"—"}
                    </TableCell>

                    {/* Row actions */}
                    {!weekIsLocked&&(
                      <TableCell align="center" sx={cellSx}>
                        {canEditRow&&(
                          <Stack direction="row" spacing={0.25} justifyContent="center">
                            <Tooltip title="Edit latest entry">
                              <IconButton size="small" onClick={()=>onEdit(firstEntry)} sx={{p:0.5}}>
                                <Edit sx={{fontSize:14,color:"#5E6C84"}}/>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete all entries for this task">
                              <IconButton size="small" onClick={()=>onDelete(firstEntry)} sx={{p:0.5}}>
                                <Delete sx={{fontSize:14,color:"#DE350B"}}/>
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}

            {/* Day totals footer row */}
            {rows.length>0&&(
              <TableRow sx={{bgcolor:"#F4F5F7"}}>
                <TableCell sx={{...cellSx,fontWeight:700,fontSize:"0.75rem",color:"#172B4D",
                  borderTop:"2px solid #DFE1E6"}}>
                  Daily Total
                </TableCell>
                {weekDates.map((d,i)=>{
                  const lh  = leaveDays[i]||0;
                  const hh  = holidayDays[i]||0;
                  const tgt = Math.max(0,8-lh-hh);
                  const tot = entries
                    .filter(e=>toLocalStr(new Date(e.timesheet_date))===d)
                    .reduce((s,e)=>s+parseFloat(e.effort_hours||0),0);
                  const met = tot>=tgt && tgt>0;
                  return (
                    <TableCell key={d} align="center"
                      sx={{...cellSx,borderLeft:"1px solid #EBECF0",borderTop:"2px solid #DFE1E6",
                        fontWeight:700,fontSize:"0.78rem",
                        color:lh>=8?"#FF8B00":tot===0?"#97A0AF":met?"#006644":"#BF2600",
                      }}>
                      {lh>=8?"🏖":tot>0?fmtHrs(tot, true):(hh>=8?"🎉":"—")}
                    </TableCell>
                  );
                })}
                {/* Week total */}
                <TableCell align="center"
                  sx={{...cellSx,borderLeft:"2px solid #DFE1E6",borderTop:"2px solid #DFE1E6",
                    fontWeight:800,fontSize:"0.85rem",
                    color:entries.reduce((s,e)=>s+parseFloat(e.effort_hours||0),0)>0?"#0052CC":"#97A0AF",
                  }}>
                  {fmtHrs(entries.reduce((s,e)=>s+parseFloat(e.effort_hours||0),0), true)}
                </TableCell>
                {!weekIsLocked&&<TableCell sx={{...cellSx,borderTop:"2px solid #DFE1E6"}}/>}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Add row button */}
      {!weekIsLocked&&(
        <Box sx={{p:1.5,borderTop:"1px solid #EBECF0",bgcolor:"#FAFBFC"}}>
          <Button size="small" startIcon={<Add sx={{fontSize:14}}/>}
            onClick={()=>onAdd(smartDefaultDate, lastEntry ? { proj_id: lastEntry.proj_id, cl_id: lastEntry.cl_id, project_task_id: lastEntry.project_task_id || null } : null)}
            sx={{textTransform:"none",color:"#0052CC",fontWeight:600,fontSize:"0.8rem",
              "&:hover":{bgcolor:"#DEEBFF"},borderRadius:1}}>
            Log Time
          </Button>
        </Box>
      )}
    </Paper>
  );
}

// ─── Approval History Tab ─────────────────────────────────────────────────────
// Shows the full audit trail across ALL weeks — who acted, when, remarks.
// Grouped by week for easy scanning.
function ApprovalHistoryTab({ empId, boId }) {
  const [history, setHistory]   = useState([]);
  const [pending, setPending]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!empId || !boId) return;
    setLoading(true);

    // Get start of current week (Sunday)
    const now = new Date();
    const day = now.getDay();
    const ws  = new Date(now); ws.setDate(now.getDate() - day); ws.setHours(0,0,0,0);
    const wsStr = `${ws.getFullYear()}-${String(ws.getMonth()+1).padStart(2,"0")}-${String(ws.getDate()).padStart(2,"0")}`;

    Promise.all([
      // Audit trail — weeks where an approver has acted
      fetch(`${BASE_URL}/api/timesheetTracker/approvals/history/${empId}/${boId}`)
        .then(r => r.json()).catch(() => []),
      // Current week entries — to detect submitted-but-not-yet-acted status
      fetch(`${BASE_URL}/api/timesheet/week/${empId}/${boId}/${wsStr}`)
        .then(r => r.ok ? r.json() : { data: [] }).catch(() => ({ data: [] })),
    ]).then(([histData, tsData]) => {
      const hist = Array.isArray(histData) ? histData : [];
      setHistory(hist);

      // Check if current week is submitted but not yet in audit history
      const tsEntries = Array.isArray(tsData?.data) ? tsData.data : Array.isArray(tsData) ? tsData : [];
      const submittedEntry = tsEntries.find(e => {
        const s = (e.approval_status || "").toLowerCase();
        return s !== "pending" && s !== "approved" && s !== "rejected" && s !== "";
      });

      if (submittedEntry) {
        // Format week dates for display
        const we = new Date(ws); we.setDate(ws.getDate() + 6);
        const fmt = (d) => `${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}/${d.getFullYear()}`;
        const wsDisplay = fmt(ws);
        const weDisplay = fmt(we);

        // Only show if not already in audit history
        const alreadyInHistory = hist.some(h => h.week_start_date === wsDisplay);
        if (!alreadyInHistory) {
          setPending([{
            week_start_date: wsDisplay,
            week_end_date: weDisplay,
            approval_status: submittedEntry.approval_status,
            proj_name: submittedEntry.proj_name,
            cl_name: submittedEntry.cl_name,
          }]);
        }
      }

      if (hist.length) setExpanded(hist[0].week_start_date);
    }).finally(() => setLoading(false));
  }, [empId, boId]);

  if (loading) return (
    <Box display="flex" justifyContent="center" py={6}>
      <CircularProgress size={28} sx={{ color:"#0052CC" }}/>
    </Box>
  );

  if (!history.length && !pending.length) return (
    <Box textAlign="center" py={6}>
      <HourglassEmpty sx={{ fontSize:40, color:"#DFE1E6" }}/>
      <Typography color="text.secondary" mt={1} variant="body2">No approval history yet</Typography>
      <Typography variant="caption" color="#C1C7D0">
        History appears here once you submit your timesheet
      </Typography>
    </Box>
  );

  // Group by week_start_date
  const byWeek = history.reduce((acc, h) => {
    const key = h.week_start_date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(h);
    return acc;
  }, {});

  const weekKeys = Object.keys(byWeek).sort((a, b) => {
    // Sort descending by date
    return new Date(b) - new Date(a);
  });

  return (
    <Stack spacing={1.5} mt={1}>
      {/* Legend */}
      <Box sx={{ p:1.5, bgcolor:"#F4F5F7", borderRadius:1.5, border:"1px solid #DFE1E6" }}>
        <Typography variant="caption" color="#5E6C84" fontWeight={600}>
          📋 Full audit trail — all approver actions across all submitted weeks
        </Typography>
      </Box>

      {/* Pending/In-progress weeks (submitted but not yet acted on) */}
      {pending.map(p => (
        <Paper key={p.week_start_date} elevation={0}
          sx={{ border:"1px solid #0052CC", borderRadius:2, overflow:"hidden" }}>
          <Box sx={{ p:1.5, display:"flex", justifyContent:"space-between", alignItems:"center", bgcolor:"#E9F2FF" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CalendarToday sx={{ fontSize:16, color:"#0052CC" }}/>
              <Box>
                <Typography variant="body2" fontWeight={700} color="#172B4D">
                  Week of {p.week_start_date} — {p.week_end_date}
                </Typography>
                <Typography variant="caption" color="#5E6C84">
                  {p.proj_name}{p.cl_name ? ` · ${p.cl_name}` : ""} · Awaiting approver action
                </Typography>
              </Box>
            </Stack>
            <StatusChip status={p.approval_status}/>
          </Box>
        </Paper>
      ))}

      {weekKeys.map(weekKey => {
        const weekEntries = byWeek[weekKey];
        const isOpen      = expanded === weekKey;
        // Determine overall week status from the latest entry
        const latestStatus = weekEntries[0]?.timesheet_status || weekEntries[0]?.approval_status;
        const weekEnd      = weekEntries[0]?.week_end_date;

        return (
          <Paper key={weekKey} elevation={0}
            sx={{ border:"1px solid #DFE1E6", borderRadius:2, overflow:"hidden" }}>
            {/* Week header — clickable to expand */}
            <Box
              onClick={() => setExpanded(isOpen ? null : weekKey)}
              sx={{
                p:1.5, display:"flex", justifyContent:"space-between", alignItems:"center",
                cursor:"pointer", bgcolor: isOpen ? "#F0F7FF" : "#FAFBFC",
                borderBottom: isOpen ? "1px solid #DFE1E6" : "none",
                "&:hover":{ bgcolor:"#F0F7FF" },
              }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CalendarToday sx={{ fontSize:16, color:"#0052CC" }}/>
                <Box>
                  <Typography variant="body2" fontWeight={700} color="#172B4D">
                    Week of {weekKey} — {weekEnd}
                  </Typography>
                  <Typography variant="caption" color="#5E6C84">
                    {weekEntries.length} project{weekEntries.length > 1 ? "s" : ""} · {weekEntries.length} action{weekEntries.length > 1 ? "s" : ""}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <StatusChip status={latestStatus}/>
                <Typography sx={{ fontSize:"0.75rem", color:"#5E6C84" }}>
                  {isOpen ? "▲" : "▼"}
                </Typography>
              </Stack>
            </Box>

            {/* Expanded entries */}
            {isOpen && (
              <Stack spacing={0} divider={<Box sx={{ borderBottom:"1px solid #EBECF0" }}/>}>
                {weekEntries.map((h, i) => (
                  <Box key={i} sx={{ px:2, py:1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                          <Typography variant="body2" fontWeight={700} color="#0052CC">
                            {h.proj_name || h.clientproject_id}
                          </Typography>
                          {h.cl_name && (
                            <Typography variant="caption" color="#97A0AF">· {h.cl_name}</Typography>
                          )}
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <AccessTime sx={{ fontSize:13, color:"#5E6C84" }}/>
                            <Typography variant="caption" color="#5E6C84">
                              {h.approval_date || "—"}
                            </Typography>
                          </Stack>
                          {(h.approver_name || h.approver_id) && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <CheckCircle sx={{ fontSize:13, color:"#36B37E" }}/>
                              <Typography variant="caption" color="#5E6C84">
                                by {h.approver_name || h.approver_id}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                        {h.approval_remarks && (
                          <Box mt={0.75} p={1} bgcolor="#F4F5F7" borderRadius={1}>
                            <Typography variant="caption" color="#5E6C84">
                              💬 <em>{h.approval_remarks}</em>
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box ml={2} flexShrink={0}>
                        <StatusChip status={h.approval_status || h.timesheet_status}/>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        );
      })}
    </Stack>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TimesheetV2() {
  const { empId, boId } = useCurrentUser();
  const [weekStart, setWeekStart]           = useState(getStartOfWeek(new Date()));
  const [entries, setEntries]               = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  const [leaveDays, setLeaveDays]           = useState({});
  const [holidayDays, setHolidayDays]       = useState({});
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [submitting, setSubmitting]         = useState(false);
  const [alert, setAlert]                   = useState({ type:"", msg:"" });
  const [logDialog, setLogDialog]           = useState({ open:false, initial:null });
  const [logDialogError, setLogDialogError] = useState("");
  const [deleteDialog, setDeleteDialog]     = useState({ open:false, entry:null });
  const [submitDialog, setSubmitDialog]     = useState(false);
  const [pickerAnchor, setPickerAnchor]     = useState(null);
  const [activeTab, setActiveTab]           = useState(0);

  const weekDates = Array.from({length:7},(_,i)=>toLocalStr(addDays(weekStart,i)));

  const showAlert = (type, msg) => {
    setAlert({type,msg});
    setTimeout(()=>setAlert({type:"",msg:""}),4500);
  };

  // ── fetch approved leaves ──────────────────────────────────────────────────
  const fetchLeaves = useCallback(async (ws) => {
    if (!empId||!boId) return;
    try {
      const res  = await fetch(`${BASE_URL}/api/leave-request/?bo_id=${boId}&employee_id=${empId}`);
      const rows = await res.json();

      // Include Approved AND any Cancellation Pending state (lock stays until cancellation is approved)
      const approved = (Array.isArray(rows)?rows:[]).filter(r => {
        if (r.delete_flag === "Y") return false;
        const s = (r.status || "").toLowerCase().trim();
        return s === "approved"
          || s.startsWith("cancellation pending")
          || (s.startsWith("waiting for") && s.includes("cancellation"));
      });

      // Derive the week dates from the ws parameter passed into this callback.
      // Using the outer `weekDates` variable would be a stale closure — it captures
      // the dates from the render in which useCallback was first memoized, not the
      // week currently being viewed. This caused leaves to appear on every occurrence
      // of the same weekday across all weeks instead of only on the specific dates.
      const currentWeekDates = Array.from({length:7}, (_,i) => toLocalStr(addDays(ws, i)));

      const toLocalDateStr = (dateStr) => {
        if (!dateStr) return null;
        const raw = String(dateStr);
        // If it's already a plain date (YYYY-MM-DD), use as-is
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
        // Otherwise parse as Date and use local date components
        const d = new Date(raw);
        return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      };

      const map = {};
      approved.forEach(leave => {
        const startStr = toLocalDateStr(leave.start_date);
        const endStr   = toLocalDateStr(leave.end_date) || startStr;

        if (!startStr) return;

        const durationType = (leave.duration_type || "").toLowerCase().trim();

        if (durationType === "specific hours") {
          // Permission leave: deduct exact hours on the single start date
          currentWeekDates.forEach((d, off) => {
            if (d === startStr) {
              map[off] = (map[off] || 0) + parseFloat(leave.total_hours || 0);
            }
          });
        } else if (durationType === "half day") {
          // Half day: deduct actual hours (e.g. 4h) — NOT 8h.
          // A day with < 8h deducted stays unlocked so the remaining hours can be logged.
          currentWeekDates.forEach((d, off) => {
            if (d === startStr) {
              map[off] = (map[off] || 0) + parseFloat(leave.total_hours || 4);
            }
          });
        } else {
          // Full / Multiple Days: deduct 8h per day across the date range
          currentWeekDates.forEach((d, off) => {
            if (d >= startStr && d <= endStr) {
              map[off] = (map[off] || 0) + 8;
            }
          });
        }
      });
      setLeaveDays(map);
    } catch(e) {
      console.error("Failed to fetch leaves:",e);
      setLeaveDays({});
    }
  }, [empId, boId]);

  // ── fetch holidays ─────────────────────────────────────────────────────────
  const fetchHolidays = useCallback(async (ws) => {
    if (!boId) return;
    try {
      const year = ws.getFullYear();
      const res  = await fetch(`${BASE_URL}/api/holidays?bo_id=${boId}&year=${year}`);
      if (!res.ok) { setHolidayDays({}); return; }
      const data = await res.json();
      const holidays = Array.isArray(data) ? data : data.data || [];

      const currentWeekDates = Array.from({length:7}, (_,i) => toLocalStr(addDays(ws, i)));

      const map = {};
      holidays.forEach(h => {
        const dateStr = h.holiday_date
          ? (/^\d{4}-\d{2}-\d{2}$/.test(String(h.holiday_date))
              ? String(h.holiday_date)
              : toLocalStr(new Date(h.holiday_date)))
          : null;
        if (!dateStr) return;

        currentWeekDates.forEach((d, off) => {
          if (d === dateStr) {
            // Full Day = 8h, Half Day = 4h
            const hrs = (h.holiday_type || "Full Day") === "Half Day" ? 4 : 8;
            map[off] = (map[off] || 0) + hrs;
          }
        });
      });
      setHolidayDays(map);
    } catch(e) {
      console.error("Failed to fetch holidays:", e);
      setHolidayDays({});
    }
  }, [boId]);

  // ── fetch timesheet ────────────────────────────────────────────────────────
  const fetchWeek = useCallback(async (ws) => {
    if (!empId||!boId) return;
    setLoading(true);
    try {
      const startStr = toLocalStr(ws);
      const tsRes = await fetch(`${BASE_URL}/api/timesheet/week/${empId}/${boId}/${startStr}`);
      const tsJson = await tsRes.json();
      setEntries(Array.isArray(tsJson)?tsJson:tsJson.data||[]);
    } catch(err) {
      console.error(err); setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [empId, boId]);

  useEffect(()=>{
    if (!empId||!boId) return;
    fetch(`${BASE_URL}/employee_projects/${empId}/${boId}`)
      .then(r=>r.json()).then(setClientProjects).catch(console.error);
  },[empId,boId]);

  useEffect(()=>{
    fetchWeek(weekStart);
    fetchLeaves(weekStart);
    fetchHolidays(weekStart);
  },[weekStart,fetchWeek,fetchLeaves,fetchHolidays]);

  // ── save ───────────────────────────────────────────────────────────────────
  const handleSaveLog = async (form) => {
    const dateObj = new Date(form.date+"T00:00:00");
    const wsDate  = new Date(weekStart.getFullYear(),weekStart.getMonth(),weekStart.getDate());
    const offset  = Math.round((new Date(dateObj.getFullYear(),dateObj.getMonth(),dateObj.getDate())-wsDate)/86400000);
    if (offset>=0&&offset<7&&(leaveDays[offset]||0)>=8) {
      setLogDialogError("This date is locked due to an approved leave. You cannot log time on leave days.");
      return;
    }
    setLogDialogError("");
    setSaving(true);
    const now = toLocalStr(new Date());
    const base = {
      bo_id:boId, employee_id:empId,
      proj_id:form.proj_id, timesheet_date:form.date,
      task_description:form.task.trim(), effort_hours:parseFloat(form.hours),
      notes:form.notes||"", project_task_id:form.project_task_id||null, delete_flag:"N", data_source:"UI",
      created_by:empId, created_on_date:now, created_at:new Date().toISOString(),
      modified_by:empId, modified_on_date:now, modified_at:new Date().toISOString(),
    };
    try {
      if (form.timesheet_id) {
        const res = await fetch(`${BASE_URL}/api/timesheet/${form.timesheet_id}/${boId}`,{
          method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify(base),
        });
        if (!res.ok) {
          const errData = await res.json().catch(()=>({}));
          throw new Error(errData.error || "Failed to update time log.");
        }
        showAlert("success","Time log updated.");
      } else {
        const res = await fetch(`${BASE_URL}/api/timesheet/create`,{
          method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(base),
        });
        if (!res.ok) {
          const errData = await res.json().catch(()=>({}));
          throw new Error(errData.error || "Failed to save time log.");
        }
        showAlert("success","Time logged.");
      }
      setLogDialog({open:false,initial:null});
      setLogDialogError("");
      fetchWeek(weekStart);
    } catch(err) {
      // Show error inside the dialog so it's visible while the dialog is still open
      setLogDialogError(err.message || "Failed to save.");
    }
    finally { setSaving(false); }
  };

  // ── delete ─────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    const entry = deleteDialog.entry;
    if (!entry) return;
    setSaving(true);
    try {
      await fetch(`${BASE_URL}/api/timesheet/${entry.timesheet_id}?bo_id=${boId}`,{method:"DELETE"});
      showAlert("success","Entry deleted.");
      setDeleteDialog({open:false,entry:null});
      fetchWeek(weekStart);
    } catch { showAlert("error","Delete failed."); }
    finally { setSaving(false); }
  };

  // ── submit ─────────────────────────────────────────────────────────────────
  const handleSubmitWeek = async () => {
    setSubmitting(true);
    try {
      const res  = await fetch(`${BASE_URL}/api/timesheet/submit`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({employee_id:empId,bo_id:boId,week_start_date:toLocalStr(weekStart)}),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error||"Submit failed");
      showAlert("success",`Submitted. Status: ${json.status}`);
      setSubmitDialog(false);
      fetchWeek(weekStart);
    } catch(err) { showAlert("error",err.message||"Submission failed."); }
    finally { setSubmitting(false); }
  };

  // ── open dialogs ───────────────────────────────────────────────────────────
  const openLogForDate = (dateStr, prefillRow = null) => {
    const cl_id   = prefillRow?.cl_id   || prefillRow?.client_id || "";
    const proj_id = prefillRow?.proj_id || "";
    const project_task_id = prefillRow?.project_task_id || null;
    // When coming from a row's + button, cl_id may be on the row object itself
    const client_id = cl_id
      ? cl_id
      : (proj_id ? (clientProjects.find(p => p.proj_id === proj_id)?.cl_id || "") : "");
    setLogDialog({open:true,initial:{
      client_id, proj_id, project_task_id,
      task:"", date:dateStr, hours:"1.00", notes:"",
    }});
  };
  const openEditLog = (entry) => {
    const proj = clientProjects.find(p=>p.proj_id===entry.proj_id);
    setLogDialog({open:true,initial:{
      timesheet_id:entry.timesheet_id,
      client_id:proj?.cl_id||entry.cl_id||"",
      proj_id:entry.proj_id, task:entry.task_description,
      project_task_id:entry.project_task_id||null,
      date:toLocalStr(new Date(entry.timesheet_date)),
      hours:parseFloat(entry.effort_hours).toFixed(2),
      notes:entry.notes||"",
    }});
  };

  // ── derived ────────────────────────────────────────────────────────────────
  // Only deduct leave/holiday hours that fall on working days (Mon–Fri, day indices 1–5).
  // Holidays or leaves on weekends (Sun=0, Sat=6) are already non-working days and
  // must not reduce the 40-hour weekly target.
  const totalLeaveHours   = parseFloat(
    Object.entries(leaveDays)
      .filter(([off]) => parseInt(off) >= 1 && parseInt(off) <= 5)
      .reduce((s,[,h])=>s+h,0)
      .toFixed(2)
  );
  const totalHolidayHours = parseFloat(
    Object.entries(holidayDays)
      .filter(([off]) => parseInt(off) >= 1 && parseInt(off) <= 5)
      .reduce((s,[,h])=>s+h,0)
      .toFixed(2)
  );
  const requiredHours     = Math.max(0, parseFloat((40 - totalLeaveHours - totalHolidayHours).toFixed(2)));
  const totalLogged       = entries.reduce((s,e)=>s+parseFloat(e.effort_hours||0),0);
  const weekMet         = totalLogged>=requiredHours;

  // Smart default date for "Log Time" buttons: latest weekday in the displayed
  // week that is ≤ today; if the whole week is in the future, fall back to Monday.
  const smartLogDate = (() => {
    const today = toLocalStr(new Date());
    for (let i = weekDates.length - 1; i >= 0; i--) {
      if (weekDates[i] <= today) return weekDates[i];
    }
    return weekDates[1] || weekDates[0];
  })();

  // Most-recently-logged entry this week (used to pre-fill new rows from header button)
  const lastWeekEntry = entries.length
    ? [...entries].sort((a, b) => new Date(b.timesheet_date) - new Date(a.timesheet_date))[0]
    : null;
  const isCurrentWeek   = getStartOfWeek(new Date()).getTime()===weekStart.getTime();
  const weekIsLocked    = entries.some(e=>isSubmittedStatus(e.approval_status));
  // Can still add new entries if at least one project is still in draft (Pending/Rejected)
  const hasAnyDraft     = entries.some(e=>e.approval_status==="Pending"||e.approval_status==="Rejected");
  const allSubmitted    = entries.length>0 && entries.every(e=>isSubmittedStatus(e.approval_status));
  const hasPending      = entries.some(e=>e.approval_status==="Pending"||e.approval_status==="Rejected");
  const canSubmit       = hasPending && weekMet && !allSubmitted;

  // Day totals for summary bar
  const dayTotals = weekDates.map(d=>
    entries.filter(e=>toLocalStr(new Date(e.timesheet_date))===d)
      .reduce((s,e)=>s+parseFloat(e.effort_hours||0),0)
  );

  const weekStatusLabel = (() => {
    if (!entries.length) return null;
    const statuses = [...new Set(entries.map(e=>e.approval_status))];
    if (statuses.every(s=>s==="Approved")) return "Approved";
    if (statuses.some(s=>s==="Rejected")) return "Rejected";
    const waiting = statuses.find(s=>s&&s.toLowerCase().includes("waiting"));
    if (waiting) return waiting;
    const pend = statuses.find(s=>s&&s.toLowerCase().startsWith("pending, "));
    if (pend) return pend;
    return null;
  })();

  return (
    <MainLayout>
      {alert.msg&&(
        <Alert severity={alert.type} onClose={()=>setAlert({type:"",msg:""})} sx={{mb:2}}>
          {alert.msg}
        </Alert>
      )}

      {/* ── Header ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton size="small" onClick={()=>setWeekStart(ws=>getStartOfWeek(addDays(ws,-7)))} sx={{color:"#5E6C84"}}>
            <ArrowBackIos fontSize="small"/>
          </IconButton>
          <Button variant="outlined" size="small"
            startIcon={<CalendarToday sx={{fontSize:15}}/>}
            onClick={(e)=>setPickerAnchor(e.currentTarget)}
            sx={{textTransform:"none",fontWeight:700,color:"#172B4D",borderColor:"#DFE1E6",
              bgcolor:"#fff",px:1.5,"&:hover":{borderColor:"#0052CC",bgcolor:"#F4F5F7"}}}>
            {formatDateRange(weekStart)}
          </Button>
          <WeekPicker weekStart={weekStart} onChange={setWeekStart}
            anchorEl={pickerAnchor} onClose={()=>setPickerAnchor(null)}/>
          <IconButton size="small" onClick={()=>setWeekStart(ws=>getStartOfWeek(addDays(ws,7)))} sx={{color:"#5E6C84"}}>
            <ArrowForwardIos fontSize="small"/>
          </IconButton>
          {!isCurrentWeek&&(
            <Button size="small" variant="text" onClick={()=>setWeekStart(getStartOfWeek(new Date()))}
              sx={{textTransform:"none",color:"#0052CC",fontSize:"0.78rem"}}>Today</Button>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {weekStatusLabel&&<StatusChip status={weekStatusLabel}/>}
          <Chip icon={<AccessTime sx={{fontSize:14}}/>}
            label={`${fmtHrs(totalLogged)} / ${fmtHrs(requiredHours)}`}
            size="small" color={weekMet?"success":"default"} sx={{fontWeight:700}}/>
          {!allSubmitted&&(
            <Button variant="outlined" startIcon={<Add/>}
              onClick={()=>openLogForDate(smartLogDate, lastWeekEntry ? { proj_id: lastWeekEntry.proj_id, cl_id: lastWeekEntry.cl_id, project_task_id: lastWeekEntry.project_task_id || null } : null)} disabled={saving}
              sx={{textTransform:"none",borderColor:"#0052CC",color:"#0052CC",fontWeight:700,
                "&:hover":{bgcolor:"#DEEBFF"}}}>
              Log Time
            </Button>
          )}
          {canSubmit&&(
            <Button variant="contained" startIcon={<Send sx={{fontSize:16}}/>}
              onClick={()=>setSubmitDialog(true)}
              sx={{textTransform:"none",bgcolor:"#0052CC","&:hover":{bgcolor:"#0065FF"},fontWeight:700}}>
              Submit Week
            </Button>
          )}
          {weekIsLocked&&!entries.every(e=>e.approval_status==="Approved")&&(
            <Chip label="Awaiting Approval" size="small"
              sx={{bgcolor:"#DEEBFF",color:"#0052CC",fontWeight:700}}/>
          )}
        </Stack>
      </Box>

      {/* ── Tabs ── */}
      <Tabs value={activeTab} onChange={(_,v)=>setActiveTab(v)}
        sx={{mb:2,minHeight:36,borderBottom:"2px solid #EBECF0",
          "& .MuiTab-root":{minHeight:36,py:0.5,fontSize:"0.82rem",textTransform:"none",fontWeight:600},
          "& .Mui-selected":{color:"#0052CC"},
          "& .MuiTabs-indicator":{bgcolor:"#0052CC",height:2}}}>
        <Tab label="Week View"/>
        <Tab label="Approval History"/>
      </Tabs>

      {activeTab===0&&(
        <>
          <WeeklySummary dayTotals={dayTotals} leaveDays={leaveDays} holidayDays={holidayDays} weekDates={weekDates}/>

          {loading?(
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={32} sx={{color:"#0052CC"}}/>
            </Box>
          ):(
            <>
            <TimesheetGrid
              entries={entries}
              weekDates={weekDates}
              leaveDays={leaveDays}
              holidayDays={holidayDays}
              onAdd={openLogForDate}
              onEdit={openEditLog}
              onDelete={(entry)=>setDeleteDialog({open:true,entry})}
              weekIsLocked={allSubmitted}
            />
            </>
          )}

          {!allSubmitted&&entries.length>0&&!weekMet&&(
            <Paper elevation={0} sx={{p:1.5,mt:1.5,border:"1px solid #FFE380",bgcolor:"#FFFAE6",borderRadius:2}}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Warning sx={{color:"#FF8B00",fontSize:18}}/>
                <Typography variant="body2" color="#FF8B00" fontWeight={600}>
                  {fmtHrs(requiredHours - totalLogged)} more needed before you can submit
                  {totalLeaveHours>0&&totalHolidayHours===0?` (${fmtHrs(totalLeaveHours)} approved leave deducted — target is ${fmtHrs(requiredHours)} this week)`:""}
                  {totalHolidayHours>0&&totalLeaveHours===0?` (${fmtHrs(totalHolidayHours)} public holiday deducted — target is ${fmtHrs(requiredHours)} this week)`:""}
                  {totalLeaveHours>0&&totalHolidayHours>0?` (${fmtHrs(totalLeaveHours)} leave + ${fmtHrs(totalHolidayHours)} holiday deducted — target is ${fmtHrs(requiredHours)} this week)`:""}
                </Typography>
              </Stack>
            </Paper>
          )}
        </>
      )}

      {activeTab===1&&<ApprovalHistoryTab empId={empId} boId={boId}/>}

      {/* ── Log Time Dialog ── */}
      <LogTimeDialog open={logDialog.open} onClose={()=>{ setLogDialog({open:false,initial:null}); setLogDialogError(""); }}
        onSave={handleSaveLog} clientProjects={clientProjects} initialData={logDialog.initial}
        boId={boId} saveError={logDialogError} saving={saving}/>

      {/* ── Submit Confirm ── */}
      <Dialog open={submitDialog} onClose={()=>setSubmitDialog(false)} maxWidth="xs"
        PaperProps={{sx:{borderRadius:2}}}>
        <DialogTitle sx={{pb:1}}>Submit Timesheet for Approval?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Submitting <strong>{fmtHrs(totalLogged)}</strong> for the week of{" "}
            <strong>{formatDateRange(weekStart)}</strong>.
          </Typography>
          <Box mt={1.5} p={1.5} bgcolor="#E9F2FF" borderRadius={1}>
            <Typography variant="caption" color="#0052CC" fontWeight={600} display="block">
              Approval flow: Technical Lead → Project Manager → HR → Admin
            </Typography>
            <Typography variant="caption" color="#5E6C84" display="block" mt={0.5}>
              Once submitted, entries are locked until rejected.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{px:3,pb:2}}>
          <Button onClick={()=>setSubmitDialog(false)} color="inherit" sx={{textTransform:"none"}}>Cancel</Button>
          <Button onClick={handleSubmitWeek} variant="contained" disabled={submitting}
            startIcon={submitting?<CircularProgress size={14} color="inherit"/>:<Send sx={{fontSize:16}}/>}
            sx={{textTransform:"none",bgcolor:"#0052CC","&:hover":{bgcolor:"#0065FF"}}}>
            {submitting?"Submitting…":"Submit for Approval"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <Dialog open={deleteDialog.open} onClose={()=>setDeleteDialog({open:false,entry:null})} maxWidth="xs"
        PaperProps={{sx:{borderRadius:2}}}>
        <DialogTitle sx={{pb:1}}>Delete Entry?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{deleteDialog.entry?.task_description}</strong> ({fmtHrs(deleteDialog.entry?.effort_hours||0)})?
          </Typography>
        </DialogContent>
        <DialogActions sx={{px:3,pb:2}}>
          <Button onClick={()=>setDeleteDialog({open:false,entry:null})} color="inherit" sx={{textTransform:"none"}}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={saving}
            sx={{textTransform:"none"}}>Delete</Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
